import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router";

import * as Api from "@/api";
import { API_FAILED } from "@/constants";
import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import Search from "@/design/components/Search";
import FlexGrow from "@/design/helper/FlexGrow";
import { setSystemSettingsPage, showSnackbar } from "@/store/reducers/app";
import { setDialDrawer, setDrawerExpanded, setDrawerOpen, setSearchValue } from "@/store/reducers/ui";
import { authenticate, signOut } from "@/utils/firebase";
import useNow from "@/utils/hooks/useNow";
import { getVersion, getVersionDate } from "@/utils/utility";
import Valkyrie, { viBars, viCircleQuestion, viCompass, viGift, viPhone } from "@sippy-platform/valkyrie";

import AppBar from "@mui/material/AppBar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { differenceInWeeks, parseISO } from "date-fns";

import ProfileMenu from "./ProfileMenu";
import SystemSettings from "./settings/Settings";

function AppBarContainer() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const matchesLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const now = useNow();

  const [profileMenu, setProfileMenu] = useState(false);

  const auth = useSelector((state) => state.auth);
  const pbx = useSelector((state) => state.domain.pbx);
  const drawerExpanded = useSelector((state) => state.ui.drawerExpanded);
  const drawerOpen = useSelector((state) => state.ui.drawerOpen);
  const dialDrawer = useSelector((state) => state.ui.dialDrawer);
  const search = useSelector((state) => state.ui.search);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const settings = useSelector((state) => state.auth.settings);
  const hasPbx = useSelector((state) => state.domain.pbx.context);
  const helpUrl = useSelector((state) => state.ui.helpUrl);

  const newUpdateButtonIsVisible = useMemo(() => {
    return settings.version !== getVersion("major") && differenceInWeeks(now, parseISO(getVersionDate())) < 1;
  }, [now, settings.version]);

  useEffect(() => {
    async function authFirebase() {
      await Promise.resolve(Api.getFirebase())
        .then((response) => {
          authenticate(response.data.firebase);
        })
        .catch((error) => {
          dispatch(showSnackbar(error.message || API_FAILED));
        });
    }

    if (hasPbx) {
      authFirebase();
    }

    return () => signOut();
  }, [dispatch, hasPbx]);

  const handleChangeSearchField = (value) => dispatch(setSearchValue(value));

  return (
    <>
      <AppBar
        position="absolute"
        sx={{
          backgroundColor: (theme) => `color-mix(in srgb, ${theme.palette.background.default} 75%, transparent)`,
          backdropFilter: "blur(15px) saturate(125%)",
          containerType: "inline-size",
          color: "inherit",
          width: {
            xs: 1,
            lg: `calc(100% - ${drawerExpanded ? 256 : 57}px)`,
          },
          border: "none",
          py: 1,
          px: 2,
          zIndex: 1200,
          left: {
            xs: 0,
            lg: drawerExpanded ? 256 : 57,
          },
        }}
        id="sippy-appbar"
      >
        <Toolbar
          disableGutters
          sx={{
            height: 48,
          }}
        >
          <IconButton
            color="inherit"
            aria-label="Menu"
            onClick={() => {
              if (matchesLgUp) {
                dispatch(setDrawerExpanded(!drawerExpanded));
              } else {
                dispatch(setDrawerOpen(!drawerOpen));
              }
            }}
            id="toggleMenuItem"
            sx={{ p: 1.5, mr: 0.5, color: "inherit" }}
          >
            <Valkyrie icon={viBars} />
          </IconButton>
          {search && (
            <Search value={searchValue} onChange={handleChangeSearchField} style={{ marginRight: 6, maxWidth: 294 }} />
          )}
          <FlexGrow />
          {matchesLgUp && (
            <>
              {newUpdateButtonIsVisible && (
                <IconButton
                  onClick={() => dispatch(setSystemSettingsPage("about"))}
                  sx={{ p: 1.5, mr: 0.5, color: "inherit" }}
                  id="aboutMenuItem"
                >
                  <Badge color="secondary" variant="dot">
                    <Valkyrie icon={viGift} />
                  </Badge>
                </IconButton>
              )}
              {hasPbx && (
                <IconButton
                  component={NavLink}
                  to="/backstage/home"
                  sx={{ p: 1.5, mr: 0.5, color: "inherit" }}
                  id="backstageMenuItem"
                  aria-label="Return to Backstage"
                >
                  <Valkyrie icon={viCompass} />
                </IconButton>
              )}
              <IconButton
                onClick={() => window.open(`${urls.docs}${helpUrl}`, "_blank")}
                sx={{ p: 1.5, mr: 0.5, color: "inherit" }}
                id="helpMenuItem"
                aria-label="Help"
              >
                <Valkyrie icon={viCircleQuestion} />
              </IconButton>
              <IconButton
                onClick={() => dispatch(setDialDrawer(!dialDrawer))}
                sx={[
                  { p: 1.5, mr: 0.5, color: "inherit" },
                  dialDrawer && {
                    bgcolor: (theme) =>
                      `rgba(${theme.vars.palette.primary.mainChannel} / ${theme.vars.palette.action.selectedOpacity})`,
                    "&:hover": {
                      bgcolor: (theme) =>
                        `rgba(${theme.vars.palette.primary.mainChannel} / calc(${theme.vars.palette.action.selectedOpacity} + ${theme.vars.palette.action.hoverOpacity}))`,
                    },
                    "&::after": {
                      transition: "all .2s ease-in-out",
                      position: "absolute",
                      backgroundColor: (theme) => theme.vars.palette.primary[500],
                      width: "calc(100% - 66%)",
                      height: 3.5,
                      borderRadius: 2,
                      left: "33%",
                      bottom: 0,
                      content: `""`,
                    },
                  },
                ]}
                id="dialMenuItem"
              >
                <Valkyrie icon={viPhone} />
              </IconButton>
            </>
          )}
          <Box
            as="a"
            onClick={(event) => setProfileMenu(event.currentTarget)}
            sx={(theme) => ({
              height: 40,
              px: 1.25,
              borderRadius: 1,
              cursor: "pointer",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              position: "relative",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,.04)",
                ...theme.applyStyles("dark", {
                  backgroundColor: "rgba(255,255,255,.08)",
                }),
              },
              ...(profileMenu && {
                bgcolor: (theme) =>
                  `rgba(${theme.vars.palette.primary.mainChannel} / ${theme.vars.palette.action.selectedOpacity})`,
                "&:hover": {
                  bgcolor: (theme) =>
                    `rgba(${theme.vars.palette.primary.mainChannel} / calc(${theme.vars.palette.action.selectedOpacity} + ${theme.vars.palette.action.hoverOpacity}))`,
                },
                "&::after": {
                  transition: "all .2s ease-in-out",
                  position: "absolute",
                  backgroundColor: theme.palette.primary[500],
                  width: "calc(100% - 66%)",
                  height: 3.5,
                  borderRadius: 2,
                  left: "33%",
                  bottom: 0,
                  content: `""`,
                },
              }),
            })}
            id="profileMenuItem"
          >
            <Box
              sx={{
                ["@container (max-width: 560px)"]: {
                  display: "none",
                },
              }}
            >
              <Typography
                sx={{
                  color: "body",
                  textTransform: "capitalize",
                  lineHeight: "1",
                  textAlign: "right",
                  mr: 1.5,
                  fontSize: ".85rem",
                  small: {
                    opacity: 0.7,
                    lineHeight: 1.4,
                  },
                }}
              >
                <span style={{ margin: 0, display: "block" }}>
                  {auth?.firstname} {auth?.lastname ?? ""}
                </span>
                {pbx?.name && <small style={{ marginTop: -2 }}>{pbx?.name}</small>}
              </Typography>
            </Box>
            <Avatar text={`${auth?.firstname} ${auth?.lastname ?? ""}`} px={24} />
          </Box>
        </Toolbar>
      </AppBar>
      <SystemSettings />
      <ProfileMenu profileMenu={profileMenu} setProfileMenu={setProfileMenu} />
    </>
  );
}

export default AppBarContainer;
